/**
 * DD-AMZ-BU-001 — Amazon business units / departments / teams for the "Business Unit"
 * attribute (Amazon Attributes). Source: the Initiatives team's "Amazon Initiatives Command
 * Center — 2026 / Acronyms" list (118 entries, received 2026-09-15, declared final by the
 * department). The field used to carry ten legacy PIM "BU" subcategories and one pick; it is
 * now this list and multi-select ("who the product is built for" often stacks).
 *
 * Option text = "CODE — Full name"; stored joined with "; " (see attributeMulti.ts).
 */
export interface AmazonBusinessUnit {
  code: string
  name: string
}

export const AMAZON_BUSINESS_UNITS: readonly AmazonBusinessUnit[] = [
  { code: '3P', name: 'Third Party' },
  { code: '3PL', name: 'Third-Party Logistics' },
  { code: 'AB', name: 'Amazon Business' },
  { code: 'ACES', name: 'Amazon Customer Excellence System' },
  { code: 'AFO', name: 'Amazon Fresh Online' },
  { code: 'AFS', name: 'Amazon Fresh Stores' },
  { code: 'AFP', name: 'Amazon Freight Partner' },
  { code: 'AGL', name: 'Amazon Grocery Logistics / Amazon Global Logistics' },
  { code: 'AGM', name: 'Assistant General Manager' },
  { code: 'AIR', name: 'Amazon Air' },
  { code: 'AM', name: 'Area Manager' },
  { code: 'AMET', name: 'Africa, Middle East and Turkey' },
  { code: 'AMPL', name: 'Amazon Partner Logistics' },
  { code: 'AMXL', name: 'Amazon Extra Large' },
  { code: 'AMZL', name: 'Amazon Logistics' },
  { code: 'ANOW / A-Now', name: 'Amazon Now' },
  { code: 'AR', name: 'Amazon Robotics' },
  { code: 'ARS', name: 'Amazon Robotics Sortable' },
  { code: 'ASIN', name: 'Amazon Standard Identification Number' },
  { code: 'ASM', name: 'Assistant Station Manager' },
  { code: 'ASU', name: 'Amazon Storage Unit' },
  { code: 'ATS', name: 'Amazon Transportation Services' },
  { code: 'AWD', name: 'Amazon Warehousing and Distribution' },
  { code: 'AWS', name: 'Amazon Web Services' },
  { code: 'BC', name: 'Business Coach' },
  { code: 'BOM', name: 'Bill of Materials' },
  { code: 'CMMS', name: 'Computerized Maintenance Management System' },
  { code: 'CRS', name: 'Centralized Routing and Scheduling' },
  { code: 'CX', name: 'Customer Experience' },
  { code: 'DA', name: 'Delivery Associate' },
  { code: 'D&C', name: 'Design & Construction' },
  { code: 'DLXS', name: 'DA Lifecycle Experience & Services' },
  { code: 'DS', name: 'Delivery Station' },
  { code: 'DSP', name: 'Delivery Service Partner' },
  { code: 'DSPX', name: 'Delivery Service Partner Experience' },
  { code: 'DT', name: 'Delivery Trainer' },
  { code: 'DTAM', name: 'Delivery Training Area Manager' },
  { code: 'EAP', name: 'Environmental Assurance and Protection' },
  { code: 'EDSP', name: 'External Delivery Service Provider' },
  { code: 'ELM', name: 'Early Life Management' },
  { code: 'ERM', name: 'Employee Relations Manager' },
  { code: 'FBA', name: 'Fulfillment by Amazon' },
  { code: 'FC', name: 'Fulfillment Center' },
  { code: 'FCAP', name: 'Fixed, Capital, Analytics and Planning' },
  { code: 'FEPPS', name: 'Front End Planning' },
  { code: 'FISX', name: 'Flex In-Station Experience' },
  { code: 'FLEX', name: 'Amazon Flex' },
  { code: 'FP&A', name: 'Financial Planning & Analysis' },
  { code: 'GCF', name: 'Global Customer Fulfillment' },
  { code: 'GES', name: 'Global Engineering Services' },
  { code: 'GESS', name: 'Global Engineering & Security Services' },
  { code: 'GF', name: 'Grocery Fulfillment' },
  { code: 'GFC', name: 'Grocery Fulfillment Center' },
  { code: 'GMH', name: 'Global Medical Health team' },
  { code: 'GPCS', name: 'Global Procurement Central Services' },
  { code: 'GPLE', name: 'Global Procurement Launch & Expansion' },
  { code: 'GPO', name: 'Global Procurement Organization' },
  { code: 'GPXTLB', name: 'Global PXT Site Lifecycle & Business Continuity' },
  { code: 'GREF', name: 'Global Real Estate and Facilities' },
  { code: 'GRS', name: 'Global Road Safety' },
  { code: 'GSF', name: 'Global Specialty Fulfillment' },
  { code: 'HRA', name: 'Human Resources Associate' },
  { code: 'HRBP', name: 'Human Resources Business Partner' },
  { code: 'HRP', name: 'Human Resources Partner' },
  { code: 'iLMDA', name: 'Integrated Last Mile Driver Academy' },
  { code: 'IVD', name: 'Integrated Virtual Delivery' },
  { code: 'IXD', name: 'Inbound Cross Dock' },
  { code: 'LAM', name: 'Learning Area Manager' },
  { code: 'LCV', name: 'Launch Configuration and Verification' },
  { code: 'LMDA', name: 'Last Mile Delivery Academy' },
  { code: 'LORE', name: 'Lead On Road Execution' },
  { code: 'LP', name: 'Loss Prevention' },
  { code: 'MHE', name: 'Material Handling Equipment' },
  { code: 'MSDS', name: 'Material Safety Data Sheet' },
  { code: 'NACF', name: 'North American Customer Fulfillment' },
  { code: 'OI', name: 'Operations Integration' },
  { code: 'OM', name: 'Operations Manager' },
  { code: 'OMR', name: 'On-site Medical Representative' },
  { code: 'ORAM', name: 'On Road Area Manager' },
  { code: 'ORS', name: 'On Road Staffing' },
  { code: 'OTR', name: 'On the Road' },
  { code: 'PA', name: 'Process Assistant' },
  { code: 'PAWS', name: 'Planning Analytics and Workforce Strategies' },
  { code: 'PM', name: 'Program Manager' },
  { code: 'PMO', name: 'Project Management Office' },
  { code: 'PO', name: 'Purchase Order' },
  { code: 'POC', name: 'Point of Contact' },
  { code: 'PSX', name: 'Partner Strategy & Experience' },
  { code: 'PXT / PxT', name: 'People Experience and Technology' },
  { code: 'QC', name: 'Quick Commerce' },
  { code: 'R2L', name: 'Rapid & Rural Logistics' },
  { code: 'REI', name: 'Retrofit Expansion and Initiatives' },
  { code: 'RIDE', name: 'Rare, Impossible, Dangerous, or Expensive' },
  { code: 'RME', name: 'Reliability and Maintenance Engineering' },
  { code: 'RMM', name: 'Regional Maintenance Manager' },
  { code: 'RSPL', name: 'Recommended Spare Parts List' },
  { code: 'RSR', name: 'Rural Super Rural' },
  { code: 'RVM', name: 'RME Vendor Management' },
  { code: 'SA', name: 'Shift Assistant' },
  { code: 'SC', name: 'Sort Center' },
  { code: 'SDC', name: 'Storage & Distribution Center' },
  { code: 'SKU', name: 'Stock Keeping Unit' },
  { code: 'SME', name: 'Subject Matter Expert' },
  { code: 'SOP', name: 'Standard Operating Procedure' },
  { code: 'SSD', name: 'Sub Same Day' },
  { code: 'STG', name: 'Safe to Go' },
  { code: 'TAM', name: 'Transportation Area Manager' },
  { code: 'TOM', name: 'Transportation Operations Management' },
  { code: 'TVM', name: 'Technical Vendor Management' },
  { code: 'UAF', name: 'Unified Alternate Fulfillment' },
  { code: 'UFF', name: 'Ultra-Fast Fresh' },
  { code: 'USNS', name: 'US Non-Sort' },
  { code: 'UTR', name: 'Under the Roof' },
  { code: 'WFM', name: 'Whole Foods Market' },
  { code: 'WHS', name: 'Workplace Health and Safety' },
  { code: 'WHSS', name: 'Workplace Health and Safety Specialist' },
  { code: 'WWGS', name: 'Worldwide Grocery Stores' },
  { code: 'WWEI', name: 'WW Engineering and Innovation' }
] as const

export function amazonBusinessUnitLabel(unit: AmazonBusinessUnit): string {
  return `${unit.code} — ${unit.name}`
}

/** The option strings the attribute schema offers, in the department's order. */
export const AMAZON_BUSINESS_UNIT_OPTIONS: readonly string[] =
  AMAZON_BUSINESS_UNITS.map(amazonBusinessUnitLabel)
